import { useState } from 'react';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import QueryStatsRoundedIcon from '@mui/icons-material/QueryStatsRounded';
import ShoppingBasketRoundedIcon from '@mui/icons-material/ShoppingBasketRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import type { SvgIconComponent } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { Tab, TabContext, TabPanel, Tabs } from '@occc/ui-design-system';

type SuggestionDrawer = {
  id: string;
  label: string;
  description: string;
  icon: SvgIconComponent;
  prompts: readonly string[];
};

type DrivePalette = {
  drive: {
    textPrimary: string;
    textSecondary: string;
  };
};

const getDrivePalette = (theme: Theme) => (theme.palette as Theme['palette'] & DrivePalette).drive;

const getTabId = (drawerId: string) => `drive-suggestion-tab-${drawerId}`;
const getTabPanelId = (drawerId: string) => `drive-suggestion-tabpanel-${drawerId}`;

const SUGGESTION_DRAWERS: readonly SuggestionDrawer[] = [
  {
    id: 'offers-campaigns',
    label: 'Offers',
    description: 'Is what I launched working?',
    icon: CampaignRoundedIcon,
    prompts: [
      'What are the best performing offers this month?',
      "What was yesterday's best performing loyalty offer?",
      'Show me a pre vs. post lift analysis for my top performing offers this month',
      "Show me a breakdown of how the top 10 brands' offers are performing this month",
    ],
  },
  {
    id: 'stores-regions',
    label: 'Stores',
    description: 'Where are my stores over- and under-performing?',
    icon: StorefrontRoundedIcon,
    prompts: [
      'Show me top and bottom performing stores last month',
      'What are the best performing stores this week?',
      'Top 3 performing stores in the Northeast over the last 14 days',
      'Which stores have the worst fuel penetration over the last 30 days?',
    ],
  },
  {
    id: 'members-enrollment',
    label: 'Members',
    description: 'How is my program growing?',
    icon: PeopleAltRoundedIcon,
    prompts: [
      'Give me member enrollment trends by channel over the last 30 days',
      'How many members completed their profile month-to-date?',
      'Daily profile completion rate for pump enrollments, last 14 days',
      'How many total active loyalty members do we have?',
    ],
  },
  {
    id: 'customer-behavior-baskets',
    label: 'Baskets',
    description: 'What are customers actually buying, and when?',
    icon: ShoppingBasketRoundedIcon,
    prompts: [
      'What are the top categories purchased from 7am to 9am over the last 30 days?',
      'What percent of fuel customers buy food over the last 30 days?',
      'Show pump-to-store conversion based on a 30-minute visit interval over the last 30 days',
      "What categories besides fuel are most common in top offer redeemers' baskets this month?",
    ],
  },
  {
    id: 'what-should-i-do-next',
    label: 'Next Steps',
    description: 'Help me decide, not just report.',
    icon: TipsAndUpdatesRoundedIcon,
    prompts: [
      'What is a good promotion to run this month to increase breakfast transactions?',
      'How do I drive more in-store transactions in my bottom 10 stores last month?',
      "Build an audience of fuel buyers who haven't been in for 30 days",
      'What seasonal promotions should I consider this month to bring shoppers back?',
    ],
  },
  {
    id: 'program-health-roi',
    label: 'Health & ROI',
    description: 'How is the loyalty program performing overall?',
    icon: QueryStatsRoundedIcon,
    prompts: [
      'Give me a month-to-date financial overview of the loyalty program: revenue, redemption cost, and ROI',
      'What are the monthly transaction trends over the last 6 months?',
      "What's the redemption rate month-to-date, and how has it shifted vs. last month?",
      'Based on loyalty data from the last 30 days, can you identify leakage and offsetting tactics?',
    ],
  },
] as const;

const sectionSx: SxProps<Theme> = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 0.75,
};

const contentContainerSx: SxProps<Theme> = {
  width: '100%',
  maxWidth: 700,
  mx: 'auto',
};

const hintSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.25,
  px: 0.5,
  alignItems: 'center',
};

const descriptionSx: SxProps<Theme> = {
  fontSize: 13,
  color: 'drive.textSecondary',
  textAlign: 'center',
};

const tabRailSx: SxProps<Theme> = {
  minHeight: 0,
  '& .MuiTabs-flexContainer': {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: 1,
    rowGap: 0.5,
    justifyContent: 'center',
  },
  '& .MuiTabs-indicator': {
    display: 'none',
  },
};

const tabSx =
  (isSelected: boolean): SxProps<Theme> =>
  (theme) => {
    const drivePalette = getDrivePalette(theme);

    return {
      minHeight: 0,
      minWidth: 0,
      px: 1.5,
      py: 1,
      borderRadius: 999,
      textTransform: 'none',
      fontSize: 13,
      fontWeight: 500,
      lineHeight: '16px',
      color: drivePalette.textPrimary,
      backgroundColor: isSelected
        ? theme.palette.common.white
        : alpha(theme.palette.common.white, 0.32),
      border: `1px solid ${alpha(drivePalette.textPrimary, isSelected ? 0.18 : 0.08)}`,
      boxShadow: 'none',
      transition: 'background-color 140ms ease, border-color 140ms ease, color 140ms ease',
      '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, isSelected ? 0.8 : 0.44),
        borderColor: alpha(drivePalette.textPrimary, 0.12),
        color: drivePalette.textPrimary,
      },
      '&.Mui-selected': {
        color: drivePalette.textPrimary,
        fontWeight: 600,
      },
      '&:focus-visible': {
        outline: `2px solid ${alpha(theme.palette.primary.main, 0.34)}`,
        outlineOffset: 2,
      },
    };
  };

const panelSx: SxProps<Theme> = (theme) => {
  const drivePalette = getDrivePalette(theme);

  return {
    width: '100%',
    borderRadius: '20px',
    border: `1px solid ${alpha(drivePalette.textPrimary, 0.08)}`,
    backgroundColor: alpha(theme.palette.common.white, 0.32),
    overflow: 'hidden',
  };
};

const panelHeaderSx: SxProps<Theme> = {
  px: 2,
  py: 1.5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 1.5,
};

const panelHeaderContentSx: SxProps<Theme> = {
  minWidth: 0,
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: 1,
};

const panelDescriptionSx: SxProps<Theme> = {
  fontSize: 15,
  lineHeight: '20px',
  fontWeight: 500,
  color: 'drive.textPrimary',
};

const panelMetaSx: SxProps<Theme> = {
  fontSize: 12,
  lineHeight: '20px',
  color: 'drive.textSecondary',
  flexShrink: 0,
  whiteSpace: 'nowrap',
  alignSelf: 'center',
};

const tabLabelWrapSx: SxProps<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.75,
};

const tabIconSx: SxProps<Theme> = {
  fontSize: 16,
};

const tabLabelTextSx: SxProps<Theme> = {
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
};

const panelIconWrapSx: SxProps<Theme> = (theme) => {
  const drivePalette = getDrivePalette(theme);

  return {
    width: 26,
    height: 26,
    borderRadius: 999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: alpha(theme.palette.common.white, 0.5),
    color: drivePalette.textPrimary,
    flexShrink: 0,
  };
};

const panelIconSx: SxProps<Theme> = {
  fontSize: 16,
};

const panelDescriptionWrapSx: SxProps<Theme> = {
  minWidth: 0,
  flex: 1,
};

const promptListSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const promptButtonSx: SxProps<Theme> = (theme) => {
  const drivePalette = getDrivePalette(theme);

  return {
    appearance: 'none',
    border: 'none',
    font: 'inherit',
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1.5,
    px: 2,
    py: 1.5,
    textAlign: 'left',
    color: theme.palette.text.primary,
    backgroundColor: 'transparent',
    borderTop: `1px solid ${alpha(drivePalette.textPrimary, 0.06)}`,
    '&:hover': {
      backgroundColor: alpha(theme.palette.common.white, 0.26),
    },
    '&:focus-visible': {
      outline: `2px solid ${alpha(theme.palette.primary.main, 0.28)}`,
      outlineOffset: -2,
    },
  };
};

const promptTextSx: SxProps<Theme> = {
  fontSize: 14,
  lineHeight: '20px',
  color: 'text.primary',
};

const promptArrowSx: SxProps<Theme> = {
  flexShrink: 0,
  color: 'drive.textSecondary',
  fontSize: 16,
};

type WelcomeSuggestionsProps = {
  onPromptClick: (prompt: string) => void;
};

export const WelcomeSuggestions = ({ onPromptClick }: WelcomeSuggestionsProps) => {
  const [activeDrawerId, setActiveDrawerId] = useState(SUGGESTION_DRAWERS[0].id);

  return (
    <Box sx={sectionSx}>
      <TabContext value={activeDrawerId}>
        <Tabs
          value={activeDrawerId}
          onChange={(_event, value: string) => setActiveDrawerId(value)}
          variant='scrollable'
          scrollButtons={false}
          aria-label='Drive Insights suggestion categories'
          sx={tabRailSx}
        >
          {SUGGESTION_DRAWERS.map((drawer) => (
            <Tab
              key={drawer.id}
              value={drawer.id}
              id={getTabId(drawer.id)}
              aria-controls={getTabPanelId(drawer.id)}
              label={
                <Box component='span' sx={tabLabelWrapSx}>
                  <drawer.icon aria-hidden='true' sx={tabIconSx} />
                  <Typography component='span' sx={tabLabelTextSx}>
                    {drawer.label}
                  </Typography>
                </Box>
              }
              disableRipple
              sx={tabSx(drawer.id === activeDrawerId)}
            />
          ))}
        </Tabs>

        <Box sx={contentContainerSx}>
          {SUGGESTION_DRAWERS.map((drawer) => (
            <TabPanel
              key={drawer.id}
              value={drawer.id}
              id={getTabPanelId(drawer.id)}
              aria-labelledby={getTabId(drawer.id)}
              sx={[panelSx, { p: 0, mt: 0.75 }]}
            >
              <Box sx={panelHeaderSx}>
                <Box sx={panelHeaderContentSx}>
                  <Box sx={panelIconWrapSx}>
                    <drawer.icon aria-hidden='true' sx={panelIconSx} />
                  </Box>
                  <Box sx={panelDescriptionWrapSx}>
                    <Typography component='p' sx={panelDescriptionSx}>
                      {drawer.description}
                    </Typography>
                  </Box>
                </Box>
                <Typography component='p' sx={panelMetaSx}>
                  {drawer.prompts.length} prompts
                </Typography>
              </Box>

              <Box sx={promptListSx}>
                {drawer.prompts.map((prompt) => (
                  <Box
                    key={prompt}
                    component='button'
                    type='button'
                    onClick={() => onPromptClick(prompt)}
                    sx={promptButtonSx}
                  >
                    <Typography component='span' sx={promptTextSx}>
                      {prompt}
                    </Typography>
                    <ArrowForwardRoundedIcon aria-hidden='true' sx={promptArrowSx} />
                  </Box>
                ))}
              </Box>
            </TabPanel>
          ))}
        </Box>
      </TabContext>

      <Box sx={contentContainerSx}>
        <Box sx={hintSx}>
          <Typography component='p' sx={descriptionSx}>
            Start with a scoped question grounded in recent loyalty workflows.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
